import { fetchJson } from "./http";
import type {
  HealthCheck,
  HierarchyResponse,
  Product,
  ProductSlug,
  Service,
  ServiceIngressToken,
  ServiceSlug,
  Tenant,
  TenantSlug,
} from "./types";

export interface TenantWithRole extends Tenant {
  role?: string;
}

export interface FullHierarchyResponse {
  tenants: TenantWithRole[];
  products: Array<Product & { tenant_slug: string }>;
  services: Array<Service & { tenant_slug: string; product_slug: string }>;
  user?: {
    id: string;
    email: string;
    username?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  smtp_configured: boolean;
}

export async function listFullHierarchy(
  signal?: AbortSignal,
): Promise<FullHierarchyResponse> {
  const res = await fetchJson<FullHierarchyResponse>("/hierarchy", { signal });
  return {
    tenants: res.tenants ?? [],
    products: res.products ?? [],
    services: res.services ?? [],
    user: res.user,
    smtp_configured: res.smtp_configured ?? false,
  };
}

export async function listTenants(signal?: AbortSignal): Promise<Tenant[]> {
  const res = await fetchJson<Tenant[] | null>("/tenants", { signal });
  return Array.isArray(res) ? res : [];
}

export async function createTenant(
  input: { slug: string; name: string; description?: string | null },
  signal?: AbortSignal,
): Promise<Tenant> {
  return fetchJson<Tenant>("/tenants", {
    method: "POST",
    body: {
      slug: input.slug,
      name: input.name,
      description: input.description ?? null,
    },
    signal,
  });
}

export async function listProductsForTenant(
  tenantSlug: TenantSlug,
  signal?: AbortSignal,
): Promise<Product[]> {
  const res = await fetchJson<Product[] | null>(
    `/tenants/${encodeURIComponent(tenantSlug)}/products`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function createProductForTenant(
  tenantSlug: TenantSlug,
  input: { slug: string; name: string; description?: string | null },
  signal?: AbortSignal,
): Promise<Product> {
  return fetchJson<Product>(`/tenants/${encodeURIComponent(tenantSlug)}/products`, {
    method: "POST",
    body: {
      slug: input.slug,
      name: input.name,
      description: input.description ?? null,
    },
    signal,
  });
}

export async function deleteProduct(
  productSlug: ProductSlug,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(`/products/${encodeURIComponent(productSlug)}`, {
    method: "DELETE",
    signal,
  });
}

export async function listServicesForProduct(
  productSlug: ProductSlug,
  signal?: AbortSignal,
): Promise<Service[]> {
  const res = await fetchJson<Service[] | null>(
    `/products/${encodeURIComponent(productSlug)}/services`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function createServiceForProduct(
  productSlug: ProductSlug,
  input: { slug: string; name: string; description?: string | null },
  signal?: AbortSignal,
): Promise<Service> {
  return fetchJson<Service>(`/products/${encodeURIComponent(productSlug)}/services`, {
    method: "POST",
    body: {
      slug: input.slug,
      name: input.name,
      description: input.description ?? null,
    },
    signal,
  });
}

export async function deleteService(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(`/services/${encodeURIComponent(serviceSlug)}`, {
    method: "DELETE",
    signal,
  });
}

export async function listIngressTokensForService(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<ServiceIngressToken[]> {
  const res = await fetchJson<ServiceIngressToken[] | null>(
    `/services/${encodeURIComponent(serviceSlug)}/ingress-tokens`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function createIngressTokenForService(
  serviceSlug: ServiceSlug,
  input: { name?: string },
  signal?: AbortSignal,
): Promise<ServiceIngressToken> {
  return fetchJson<ServiceIngressToken>(
    `/services/${encodeURIComponent(serviceSlug)}/ingress-tokens`,
    {
      method: "POST",
      body: {
        name: input.name ?? "default",
      },
      signal,
    },
  );
}

export async function revokeIngressTokenForService(
  serviceSlug: ServiceSlug,
  tokenId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/ingress-tokens/${encodeURIComponent(tokenId)}/revoke`,
    { method: "POST", signal },
  );
}

export async function getTenantHierarchy(
  tenantSlug: TenantSlug,
  signal?: AbortSignal,
): Promise<HierarchyResponse> {
  return fetchJson<HierarchyResponse>(`/tenants/${encodeURIComponent(tenantSlug)}/hierarchy`, {
    signal,
  });
}

export async function getProductHierarchy(
  productSlug: ProductSlug,
  signal?: AbortSignal,
): Promise<HierarchyResponse> {
  return fetchJson<HierarchyResponse>(`/products/${encodeURIComponent(productSlug)}/hierarchy`, {
    signal,
  });
}

export async function getServiceHierarchy(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<HierarchyResponse> {
  return fetchJson<HierarchyResponse>(`/services/${encodeURIComponent(serviceSlug)}/hierarchy`, {
    signal,
  });
}

export async function listHealthChecksForService(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<HealthCheck[]> {
  const res = await fetchJson<HealthCheck[] | null>(
    `/services/${encodeURIComponent(serviceSlug)}/health-checks`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function createHealthCheckForService(
  serviceSlug: ServiceSlug,
  input: {
    name: string;
    url: string;
    probe_type: "liveness" | "liveness_dotnet_health";
    probe_interval_ms: number;
    enabled?: boolean;
  },
  signal?: AbortSignal,
): Promise<HealthCheck> {
  return fetchJson<HealthCheck>(`/services/${encodeURIComponent(serviceSlug)}/health-checks`, {
    method: "POST",
    body: {
      name: input.name,
      url: input.url,
      probe_type: input.probe_type,
      probe_interval_ms: input.probe_interval_ms,
      enabled: input.enabled ?? true,
    },
    signal,
  });
}

export async function updateHealthCheckForService(
  serviceSlug: ServiceSlug,
  healthCheckId: string,
  input: {
    name?: string;
    url?: string;
    probe_type?: "liveness" | "liveness_dotnet_health";
    probe_interval_ms?: number;
    enabled?: boolean;
  },
  signal?: AbortSignal,
): Promise<HealthCheck> {
  return fetchJson<HealthCheck>(
    `/services/${encodeURIComponent(serviceSlug)}/health-checks/${encodeURIComponent(healthCheckId)}`,
    {
      method: "PATCH",
      body: input,
      signal,
    },
  );
}

export async function deleteHealthCheckForService(
  serviceSlug: ServiceSlug,
  healthCheckId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/health-checks/${encodeURIComponent(healthCheckId)}`,
    { method: "DELETE", signal },
  );
}

// ---- alerts ----

export interface Alert {
  id: string;
  service_id: string;
  name: string;
  enabled: boolean;
  source_type: "health" | "metric";
  source_config: Record<string, unknown>;
  condition_type: "immediate" | "prolonged";
  condition_config: Record<string, unknown>;
  recurrence_type: "none" | "every_n_hours" | "daily_at";
  recurrence_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  channel_ids?: string[];
}

export interface ServiceAlertChannel {
  id: string;
  service_id: string;
  name: string;
  channel_type: "webhook" | "ntfy" | "email" | "telegram";
  channel_config: Record<string, unknown>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export async function listAlertsForService(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<Alert[]> {
  const res = await fetchJson<(Alert & { channel_ids?: string[] })[] | null>(
    `/services/${encodeURIComponent(serviceSlug)}/alerts`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function getAlertForService(
  serviceSlug: ServiceSlug,
  alertId: string,
  signal?: AbortSignal,
): Promise<Alert & { channel_ids: string[] }> {
  return fetchJson<Alert & { channel_ids: string[] }>(
    `/services/${encodeURIComponent(serviceSlug)}/alerts/${encodeURIComponent(alertId)}`,
    { signal },
  );
}

export async function createAlertForService(
  serviceSlug: ServiceSlug,
  input: {
    name: string;
    source_type: "health" | "metric";
    source_config?: Record<string, unknown>;
    condition_type: "immediate" | "prolonged";
    condition_config?: Record<string, unknown>;
    recurrence_type?: "none" | "every_n_hours" | "daily_at";
    recurrence_config?: Record<string, unknown>;
    enabled?: boolean;
    channel_ids?: string[];
  },
  signal?: AbortSignal,
): Promise<Alert> {
  return fetchJson<Alert>(`/services/${encodeURIComponent(serviceSlug)}/alerts`, {
    method: "POST",
    body: {
      name: input.name,
      source_type: input.source_type,
      source_config: input.source_config ?? {},
      condition_type: input.condition_type,
      condition_config: input.condition_config ?? {},
      recurrence_type: input.recurrence_type ?? "none",
      recurrence_config: input.recurrence_config ?? {},
      enabled: input.enabled ?? true,
      channel_ids: input.channel_ids ?? [],
    },
    signal,
  });
}

export async function updateAlertForService(
  serviceSlug: ServiceSlug,
  alertId: string,
  input: Partial<
    Pick<
      Alert,
      | "name"
      | "enabled"
      | "source_type"
      | "source_config"
      | "condition_type"
      | "condition_config"
      | "recurrence_type"
      | "recurrence_config"
    >
  > & { channel_ids?: string[] },
  signal?: AbortSignal,
): Promise<Alert> {
  return fetchJson<Alert>(
    `/services/${encodeURIComponent(serviceSlug)}/alerts/${encodeURIComponent(alertId)}`,
    { method: "PATCH", body: input, signal },
  );
}

export async function deleteAlertForService(
  serviceSlug: ServiceSlug,
  alertId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/alerts/${encodeURIComponent(alertId)}`,
    { method: "DELETE", signal },
  );
}

export async function setAlertChannelAssignments(
  serviceSlug: ServiceSlug,
  alertId: string,
  channelIds: string[],
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/alerts/${encodeURIComponent(alertId)}/channel-assignments`,
    { method: "PATCH", body: { channel_ids: channelIds }, signal },
  );
}

// ---- service-level channels ----

export async function listChannelsForService(
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<ServiceAlertChannel[]> {
  const res = await fetchJson<ServiceAlertChannel[] | null>(
    `/services/${encodeURIComponent(serviceSlug)}/channels`,
    { signal },
  );
  return Array.isArray(res) ? res : [];
}

export async function getChannelForService(
  serviceSlug: ServiceSlug,
  channelId: string,
  signal?: AbortSignal,
): Promise<ServiceAlertChannel> {
  return fetchJson<ServiceAlertChannel>(
    `/services/${encodeURIComponent(serviceSlug)}/channels/${encodeURIComponent(channelId)}`,
    { signal },
  );
}

export async function createChannelForService(
  serviceSlug: ServiceSlug,
  input: {
    name?: string;
    channel_type: "webhook" | "ntfy" | "email" | "telegram";
    channel_config?: Record<string, unknown>;
    enabled?: boolean;
  },
  signal?: AbortSignal,
): Promise<ServiceAlertChannel> {
  return fetchJson<ServiceAlertChannel>(
    `/services/${encodeURIComponent(serviceSlug)}/channels`,
    {
      method: "POST",
      body: {
        name: input.name ?? input.channel_type,
        channel_type: input.channel_type,
        channel_config: input.channel_config ?? {},
        enabled: input.enabled ?? true,
      },
      signal,
    },
  );
}

export async function updateChannelForService(
  serviceSlug: ServiceSlug,
  channelId: string,
  input: { name?: string; enabled?: boolean; channel_config?: Record<string, unknown> },
  signal?: AbortSignal,
): Promise<ServiceAlertChannel> {
  return fetchJson<ServiceAlertChannel>(
    `/services/${encodeURIComponent(serviceSlug)}/channels/${encodeURIComponent(channelId)}`,
    { method: "PATCH", body: input, signal },
  );
}

export async function deleteChannelForService(
  serviceSlug: ServiceSlug,
  channelId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/channels/${encodeURIComponent(channelId)}`,
    { method: "DELETE", signal },
  );
}

export async function testChannelForService(
  serviceSlug: ServiceSlug,
  channelId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/services/${encodeURIComponent(serviceSlug)}/channels/${encodeURIComponent(channelId)}/test`,
    { method: "POST", signal },
  );
}

export async function getSMTPConfigured(signal?: AbortSignal): Promise<{ smtp_configured: boolean }> {
  return fetchJson<{ smtp_configured: boolean }>("/config/smtp-configured", { signal });
}
